import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, Download, Upload, RefreshCw, Trash2, X as XIcon } from "lucide-react";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatAED, formatDate, daysBetween } from "@/lib/format";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ProComplianceForm, { PRO_TYPES, PRO_STATUSES } from "@/components/pro/ProComplianceForm";
import ProImportDialog from "@/components/pro/ProImportDialog";
import logger from "@/lib/logger";

function ExpiryBadge({ date: iso, status }) {
  if (status === "Completed") return <span className="dl-green px-2 py-0.5 rounded-md text-xs font-medium font-mono-num">Completed</span>;
  if (status === "Cancelled") return <span className="dl-slate px-2 py-0.5 rounded-md text-xs font-medium">Cancelled</span>;
  const d = daysBetween(iso);
  if (d === null) return null;
  let cls = "dl-slate";
  if (d < 0) cls = "dl-red";
  else if (d <= 7) cls = "dl-orange";
  else if (d <= 30) cls = "dl-yellow";
  const txt = d < 0 ? `${Math.abs(d)}d expired` : d === 0 ? "Today" : `${d}d`;
  return <span className={`${cls} px-2 py-0.5 rounded-md text-xs font-medium font-mono-num`}>{txt}</span>;
}

function InvoiceStatusCell({ record, onChanged }) {
  const [saving, setSaving] = React.useState(false);
  const map = {
    "Invoice Pending": "bg-amber-50 text-amber-700 border-amber-200",
    "Invoiced": "bg-blue-50 text-blue-700 border-blue-200",
    "Partly Paid": "bg-orange-50 text-orange-700 border-orange-200",
    "Paid": "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const current = record.action_2 || "Invoice Pending";
  const onSelect = async (v) => {
    if (v === current) return;
    setSaving(true);
    try {
      await api.patch(`/pro-compliances/${record.id}`, { action_2: v });
      toast.success(`Invoice status → ${v}`);
      onChanged?.();
    } catch (e) {
      toast.error("Update failed");
    } finally { setSaving(false); }
  };
  return (
    <Select value={current} onValueChange={onSelect} disabled={saving}>
      <SelectTrigger
        className={`h-7 px-2 text-xs w-[140px] border ${map[current] || map["Invoice Pending"]}`}
        data-testid={`pro-invoice-status-${record.id}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {["Invoice Pending", "Invoiced", "Partly Paid", "Paid"].map((s) => (
          <SelectItem key={s} value={s}>{s}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ActionBadge({ value }) {
  const v = value || "Invoice Pending";
  const map = {
    "Not Invoiced": "bg-slate-100 text-slate-700 border-slate-200",
    "Invoice Pending": "bg-amber-50 text-amber-700 border-amber-200",
    "Invoiced": "bg-blue-50 text-blue-700 border-blue-200",
    "Partly Paid": "bg-orange-50 text-orange-700 border-orange-200",
    "Paid": "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${map[v] || map["Invoice Pending"]}`}>{v}</span>;
}

const PAGE_SIZE = 100;

export default function ProCompliances() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [expiringWithin, setExpiringWithin] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [page, setPage] = useState(1);

  const canDelete = user?.role === "administrator";

  const fetchUsers = useCallback(async () => {
    try { const { data } = await api.get("/users"); setUsers(data); } catch (err) { logger.error("Failed to load users:", err); }
  }, []);
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE, sort: "expiry_date", order: "asc" };
      if (q) params.q = q;
      if (type) params.compliance_type = type;
      if (status) params.status = status;
      if (expiringWithin !== "") params.expiring_within = Number(expiringWithin);
      if (overdueOnly) params.overdue_only = true;
      const { data } = await api.get("/pro-compliances", { params });
      setItems(data.items); setTotal(data.total);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, [q, type, status, expiringWithin, overdueOnly, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const allSelected = items.length > 0 && items.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allSelected) items.forEach((c) => n.delete(c.id));
    else items.forEach((c) => n.add(c.id));
    return n;
  });
  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const clear = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} PRO compliance(s)?`)) return;
    try {
      const { data } = await api.post("/pro-compliances/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted}`);
      clear(); fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete PRO record for ${c.client_name}?`)) return;
    try {
      await api.delete(`/pro-compliances/${c.id}`);
      toast.success("Deleted"); fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AppLayout
      title="PRO Compliance"
      actions={
        <>
          <Button variant="outline" onClick={fetchItems} data-testid="refresh-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
          <Button variant="outline" onClick={() => window.open(`${API_BASE}/pro-compliances/export.csv`, "_blank")} data-testid="pro-export-csv"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="pro-import-btn"><Upload className="w-4 h-4 mr-1.5" /> Import CSV</Button>
          <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-pro-btn"><Plus className="w-4 h-4 mr-1.5" /> New PRO</Button>
        </>
      }
    >
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search client…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="pl-9" data-testid="pro-filter-search" />
          </div>
          <Select value={type || "all"} onValueChange={(v) => { setType(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[190px]" data-testid="pro-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {PRO_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[150px]" data-testid="pro-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {PRO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <button
            onClick={() => { setOverdueOnly((v) => !v); setPage(1); }}
            className={cn("px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
              overdueOnly ? "bg-red-600 text-white border-red-600" : "bg-white border-slate-300 text-slate-700")}
            data-testid="pro-filter-overdue"
          >
            Expired only
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5" data-testid="pro-bulk-bar">
          <div className="text-sm text-blue-900"><span className="font-semibold font-mono-num">{selected.size}</span> record(s) selected</div>
          <div className="flex items-center gap-2">
            {canDelete && (
              <Button variant="destructive" size="sm" onClick={bulkDelete} data-testid="pro-bulk-delete">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete selected
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={clear} data-testid="pro-bulk-clear">
              <XIcon className="w-4 h-4 mr-1.5" /> Clear
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="px-3 w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="pro-select-all" />
              </TableHead>
              <TableHead className="px-4">Type</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Responsible</TableHead>
              <TableHead className="text-right">Prof. Fees</TableHead>
              <TableHead>Expiry Date</TableHead>
              <TableHead>Reminder</TableHead>
              <TableHead>Days</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Invoice Status</TableHead>
              <TableHead>Remark</TableHead>
              <TableHead className="text-right pr-4">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={12} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={12} className="text-center py-16 text-slate-500">No PRO compliance records yet.</TableCell></TableRow>
            ) : items.map((c) => (
              <TableRow key={c.id} className="data-row" data-testid={`pro-row-${c.id}`}>
                <TableCell className="px-3">
                  <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} data-testid={`pro-check-${c.id}`} />
                </TableCell>
                <TableCell className="px-4 text-[11px] uppercase tracking-widest font-semibold text-slate-700">{c.compliance_type}</TableCell>
                <TableCell className="font-medium text-slate-900">{c.client_name}</TableCell>
                <TableCell className="text-sm text-slate-700">{c.responsible_person?.name || "—"}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-800">{formatAED(c.professional_fees)}</TableCell>
                <TableCell className="font-mono-num text-slate-800">{formatDate(c.expiry_date)}</TableCell>
                <TableCell className="text-sm font-mono-num text-slate-700">{c.reminder_days ?? 30}d</TableCell>
                <TableCell><ExpiryBadge date={c.expiry_date} status={c.status} /></TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-700">{c.status}</span></TableCell>
                <TableCell><InvoiceStatusCell record={c} onChanged={fetchItems} /></TableCell>
                <TableCell className="text-sm text-slate-600 max-w-[220px] truncate" title={c.remark}>{c.remark || "—"}</TableCell>
                <TableCell className="text-right pr-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`pro-actions-${c.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`pro-edit-${c.id}`}>Edit</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-widest">Quick status</DropdownMenuLabel>
                      {PRO_STATUSES.map((s) => (
                        <DropdownMenuItem key={s} onClick={async () => { await api.patch(`/pro-compliances/${c.id}`, { status: s }); toast.success("Status updated"); fetchItems(); }}>{s}</DropdownMenuItem>
                      ))}
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600 focus:text-red-700" onClick={() => remove(c)} data-testid={`pro-delete-${c.id}`}>Delete</DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-sm text-slate-600">
          <div>Showing <span className="font-medium">{items.length}</span> of <span className="font-medium font-mono-num">{total}</span> records</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span className="text-xs px-2 font-mono-num">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>

      <ProComplianceForm open={showForm} onClose={() => { setShowForm(false); setEditing(null); }} initial={editing} users={users} onSaved={fetchItems} />
      <ProImportDialog open={showImport} onClose={() => setShowImport(false)} onDone={fetchItems} />
    </AppLayout>
  );
}
